import { Shortcut, Summary } from '@renderer/chat'
import type { Skill } from '@renderer/chat/types'
import { AudioNoteEmailModal } from './modals/audio-note-email-modal'
import { ShortcutEditorModal } from './modals/shortcut-editor-modal'
import { SkillEditorModal } from './modals/skill-editor-modal'
import { SkillsMarketplaceModal } from './modals/skills-marketplace-modal'
import { HighlightsStackModal } from './modals/highlights-stack-modal'
import { DeleteConfirmationModal } from './modals/delete-confirmation-modal'
import { SummaryViewModal } from './modals/summary-view-modal'
import { SummaryFeedbackModal } from './modals/summary-feedback-modal'
import { LogoutModal } from './modals/logout-modal'
import { HotkeysOverviewModal } from './modals/hotkeys-overview-modal'
import { LoginRequiredModal } from './modals/login-required-modal'
import { HowYouWorkWelcomeModal } from '@renderer/windows/features/settings/tabs/how-you-work/nested/welcome-modal'
import { DiscardChangesModal } from './modals/discard-changes-modal'
import { ChangelogModal } from '@renderer/presentations/modals/ChangelogModal'

/**
 * Helper function to define a modal with its context type
 * If no context type is provided, the modal doesn't require context
 */
function defineModal<TContext = void>(component: () => JSX.Element | null, options?: { confirm?: boolean }) {
  return {
    render: component,
    context: {} as TContext,
    confirm: options?.confirm,
  }
}

/**
 * Central registry of all modals with their components and context types
 */
export const modalRegistry = {
  'audio-note-email': defineModal<{ audioNoteId: string }>(AudioNoteEmailModal),
  'highlights-stack': defineModal<{ group: string }>(HighlightsStackModal),
  'shortcut-editor': defineModal<{ shortcut?: Shortcut }>(ShortcutEditorModal),
  'skill-editor': defineModal<{ skill?: Skill }>(SkillEditorModal),
  'skills-marketplace': defineModal(SkillsMarketplaceModal),
  'delete-confirmation': defineModal<{
    confirmationText?: string
    onConfirm: () => void
    isPending?: boolean
  }>(DeleteConfirmationModal),
  'summary-view': defineModal<{ summaryId: string }>(SummaryViewModal),
  'summary-feedback': defineModal<{
    rating: 'positive' | 'negative'
    summary?: Summary
  }>(SummaryFeedbackModal),
  logout: defineModal(LogoutModal, { confirm: true }),
  'hotkeys-overview': defineModal(HotkeysOverviewModal),
  'login-required': defineModal(LoginRequiredModal, { confirm: true }),
  changelog: defineModal<{ useLatest?: boolean }>(ChangelogModal),
  'how-you-work-welcome': defineModal(HowYouWorkWelcomeModal),
  'discard-changes': defineModal<{ onConfirm: () => void }>(DiscardChangesModal, { confirm: true }),
} as const
